/**
 * Sidebar navigation tree as pure data.
 *
 * Behaviour (onSelect handlers, lazy children, permission filtering) is attached
 * by `useNavTree`, so this module stays trivially testable and free of hooks.
 *
 * @module NavItems
 */

import { createElement } from "react"
import type { LucideIcon } from "lucide-react"
import {
  AlarmClock,
  Book,
  BookOpen,
  ChartColumnBig,
  FlaskConical,
  House,
  SquareCheckBig,
  Store,
  Users,
  Zap,
} from "lucide-react"
import type { NavSection } from "@strivelabs/ui-blocks/v2"
import { reportsMatcher, startsWith } from "./matchNav"

/**
 * Renders a nav icon at the shared 16px size.
 *
 * These were imported SVGs from `assets/navbar/` until the v2 sidebar landed.
 * Those assets are solid `fill="white"` glyphs drawn for the old dark floating
 * rail: invisible on a light surface, and still the wrong species once tinted —
 * the design calls for outline strokes. Lucide gives outlines that inherit
 * `currentColor` directly, so the active state and dark mode come for free and
 * the CSS-mask workaround the assets needed is gone.
 */
const navIcon = (icon: LucideIcon) => createElement(icon, { size: 16 })

/** Ids of items only StriveLabs internal users see. */
export const INTERNAL_NAV_IDS = ["marketplace", "experiments"] as const

/**
 * Sidebar tree for a signed-in, non-guest user.
 *
 * One unlabelled section, matching the order the floating rail shipped. The
 * shape supports labelled groups at any depth — see the nested agent categories
 * `useNavTree` builds under My Workforce — but the top level stays flat until
 * there is a reason to group it.
 */
export const NAV_ITEMS: NavSection[] = [
  {
    id: "main",
    items: [
      {
        id: "home",
        label: "Home",
        href: "/ui",
        icon: navIcon(House),
        match: pathname => pathname === "/ui",
      },
      {
        id: "knowledge-hub",
        label: "Unified marketing database",
        href: "/ui/knowledge-hub",
        icon: navIcon(Book),
        match: startsWith("/ui/knowledge-hub"),
      },
      {
        id: "reports",
        label: "Reports",
        href: "/ui/reports",
        icon: navIcon(ChartColumnBig),
        match: reportsMatcher,
      },
      {
        id: "routines",
        label: "Routines",
        href: "/ui/routines",
        icon: navIcon(AlarmClock),
        match: startsWith("/ui/routines"),
      },
      {
        id: "tasks",
        label: "Tasks",
        href: "/ui/tasks",
        icon: navIcon(SquareCheckBig),
        match: startsWith("/ui/tasks"),
      },
      {
        id: "skills",
        label: "Skills",
        href: "/ui/skills",
        icon: navIcon(Zap),
        match: startsWith("/ui/skills"),
      },
      {
        id: "glossary",
        label: "Glossary",
        href: "/ui/glossary",
        icon: navIcon(BookOpen),
        match: startsWith("/ui/glossary"),
      },
      {
        id: "workforce",
        label: "My Workforce",
        href: "/ui/my-workforce",
        icon: navIcon(Users),
        match: startsWith("/ui/my-workforce"),
      },
    ],
  },
  {
    id: "internal",
    label: "Internal",
    items: [
      {
        id: "marketplace",
        label: "Marketplace",
        href: "/ui/marketplace",
        icon: navIcon(Store),
      },
      {
        id: "experiments",
        label: "Experiments",
        href: "/ui/experiments",
        icon: navIcon(FlaskConical),
      },
    ],
  },
]

/**
 * Nav shown to guests — a single Reports entry. It stays active while viewing a
 * report-surfaced agent (for example AEO), so guests reach those reports from
 * within the Reports section rather than a separate nav item.
 */
export const GUEST_NAV_ITEMS: NavSection[] = [
  {
    id: "top",
    items: [
      {
        id: "reports",
        label: "Reports",
        href: "/ui/reports",
        icon: navIcon(ChartColumnBig),
        match: reportsMatcher,
      },
    ],
  },
]
